import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Transaction } from '@/types';

export interface BillPDFData {
  transaction: Transaction;
  client?: {
    name: string;
    email?: string;
    phone?: string;
    documentNumber?: string;
    address?: {
      street?: string;
      number?: string;
      complement?: string;
      neighborhood?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    };
  };
  bankDetails?: {
    bank?: string;
    agency?: string;
    account?: string;
    pixKey?: string;
  };
  billNumber: string;
  issueDate: Date;
  companyName: string;
  companyLogo?: string;
  companyDocument?: string;
  companyAddress?: {
    street?: string;
    number?: string;
    complement?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
}

/**
 * Generate a professional bill PDF
 */
export async function generateBillPDF(data: BillPDFData): Promise<void> {
  try {
    const { transaction, client, bankDetails, billNumber, issueDate, companyName, companyLogo, companyDocument, companyAddress } = data;

    // Create new PDF document
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    // Get page dimensions
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPosition = margin;

    // Add company logo if available
    if (companyLogo) {
      try {
        // Fetch the logo image
        const logoImg = new Image();
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
          logoImg.src = companyLogo;
        });

        // Add logo to PDF (centered, max 30mm width)
        const maxLogoWidth = 30;
        const maxLogoHeight = 15;
        const logoWidth = Math.min(maxLogoWidth, (logoImg.width * maxLogoHeight) / logoImg.height);
        const logoHeight = (logoImg.height * logoWidth) / logoImg.width;
        const logoX = (pageWidth - logoWidth) / 2;

        doc.addImage(logoImg, 'PNG', logoX, yPosition, logoWidth, logoHeight);
        yPosition += logoHeight + 10;
      } catch (error) {
        console.warn('Failed to add logo to PDF:', error);
        // Continue without logo if it fails
      }
    }

    // Add company name as header
    doc.setFontSize(20);
    doc.setTextColor(0);
    doc.setFont('helvetica', 'bold');
    doc.text(companyName, margin, yPosition);
    yPosition += 15;

    // Add company document if available
    if (companyDocument) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100);
      doc.text(`CNPJ: ${companyDocument}`, margin, yPosition);
      yPosition += 10;
    }

    // Add bill title
    doc.setFontSize(24);
    doc.setTextColor(217, 119, 6); // terracotta color
    doc.setFont('helvetica', 'bold');
    doc.text('COBRANÇA', margin, yPosition);
    yPosition += 10;

    // Add bill number and issue date
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.setFont('helvetica', 'normal');
    doc.text(`Nº ${billNumber}`, margin, yPosition);
    yPosition += 7;
    doc.text(
      `Emitido em: ${issueDate.toLocaleDateString('pt-BR')}`,
      margin,
      yPosition
    );
    yPosition += 15;

    // Add separator line
    doc.setDrawColor(200);
    doc.setLineWidth(0.5);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 10;

    // Calculate days until due
    const getDaysUntilDue = () => {
      if (!transaction.due_date) return null;
      const due = new Date(transaction.due_date);
      const today = new Date();
      const diffTime = due.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    };

    const daysUntilDue = getDaysUntilDue();

    // Add payment status with color coding
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    if (transaction.is_paid) {
      doc.setTextColor(0, 150, 0);
      doc.text('✓ PAGO', margin, yPosition);
    } else if (daysUntilDue !== null) {
      if (daysUntilDue < 0) {
        doc.setTextColor(220, 38, 38); // red for overdue
        doc.text('⚠ VENCIDO', margin, yPosition);
      } else if (daysUntilDue <= 7) {
        doc.setTextColor(245, 158, 11); // yellow for due soon
        doc.text('⏰ VENCE EM BREVE', margin, yPosition);
      } else {
        doc.setTextColor(217, 119, 6); // terracotta for pending
        doc.text('◔ A PAGAR', margin, yPosition);
      }
    } else {
      doc.setTextColor(217, 119, 6);
      doc.text('◔ A PAGAR', margin, yPosition);
    }
    yPosition += 15;

    // Add transaction details section
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.setFont('helvetica', 'bold');
    doc.text('Detalhes da Cobrança', margin, yPosition);
    yPosition += 10;

    // Transaction info in two columns
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(100);

    // Left column
    let leftX = margin;
    let rightX = pageWidth / 2 + 10;

    // Description
    doc.text('Descrição:', leftX, yPosition);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    doc.text(transaction.description, leftX + 35, yPosition);
    yPosition += 8;

    // Amount
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text('Valor:', leftX, yPosition);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(217, 119, 6);
    doc.text(formatCurrency(transaction.amount), leftX + 35, yPosition);
    yPosition += 8;

    // Due date
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text('Data de Vencimento:', leftX, yPosition);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    const dueDate = transaction.due_date
      ? new Date(transaction.due_date).toLocaleDateString('pt-BR')
      : 'Não definido';
    doc.text(dueDate, leftX + 35, yPosition);
    yPosition += 8;

    // Days until due
    if (daysUntilDue !== null) {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100);
      doc.text('Status:', leftX, yPosition);
      doc.setFont('helvetica', 'bold');
      if (daysUntilDue < 0) {
        doc.setTextColor(220, 38, 38);
        doc.text(`${Math.abs(daysUntilDue)} dias de atraso`, leftX + 35, yPosition);
      } else if (daysUntilDue === 0) {
        doc.setTextColor(245, 158, 11);
        doc.text('Vence hoje', leftX + 35, yPosition);
      } else {
        doc.setTextColor(0, 150, 0);
        doc.text(`Vence em ${daysUntilDue} dias`, leftX + 35, yPosition);
      }
      yPosition += 8;
    }

    // Category (if exists)
    if (transaction.category) {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100);
      doc.text('Categoria:', leftX, yPosition);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0);
      doc.text(transaction.category, leftX + 35, yPosition);
      yPosition += 8;
    }

    yPosition += 10;

    // Client information section
    if (client) {
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.setFont('helvetica', 'bold');
      doc.text('Dados do Cliente', margin, yPosition);
      yPosition += 10;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(100);

      // Name
      doc.text('Nome:', leftX, yPosition);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0);
      doc.text(client.name, leftX + 35, yPosition);
      yPosition += 8;

      // Document
      if (client.documentNumber) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100);
        doc.text('CPF/CNPJ:', leftX, yPosition);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0);
        doc.text(client.documentNumber, leftX + 35, yPosition);
        yPosition += 8;
      }

      // Email
      if (client.email) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100);
        doc.text('E-mail:', leftX, yPosition);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0);
        doc.text(client.email, leftX + 35, yPosition);
        yPosition += 8;
      }

      // Phone
      if (client.phone) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100);
        doc.text('Telefone:', leftX, yPosition);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0);
        doc.text(client.phone, leftX + 35, yPosition);
        yPosition += 8;
      }

      // Address
      if (client.address) {
        yPosition += 5;
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100);
        doc.text('Endereço:', leftX, yPosition);
        yPosition += 6;

        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0);
        const addressLines = [
          [client.address.street, client.address.number, client.address.complement].filter(Boolean).join(', '),
          client.address.neighborhood,
          [client.address.city, client.address.state, client.address.zipCode].filter(Boolean).join(' - ')
        ].filter(Boolean);

        addressLines.forEach((line) => {
          doc.text(line, leftX + 35, yPosition);
          yPosition += 6;
        });
      }

      yPosition += 10;
    }

    // Payment information section
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.setFont('helvetica', 'bold');
    doc.text('Dados para Pagamento', margin, yPosition);
    yPosition += 10;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(100);

    if (bankDetails) {
      // Bank
      if (bankDetails.bank) {
        doc.text('Banco:', leftX, yPosition);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0);
        doc.text(bankDetails.bank, leftX + 35, yPosition);
        yPosition += 8;
      }

      // Agency
      if (bankDetails.agency) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100);
        doc.text('Agência:', leftX, yPosition);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0);
        doc.text(bankDetails.agency, leftX + 35, yPosition);
        yPosition += 8;
      }

      // Account
      if (bankDetails.account) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100);
        doc.text('Conta:', leftX, yPosition);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0);
        doc.text(bankDetails.account, leftX + 35, yPosition);
        yPosition += 8;
      }

      // PIX
      if (bankDetails.pixKey) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100);
        doc.text('Chave PIX:', leftX, yPosition);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0);
        doc.text(bankDetails.pixKey, leftX + 35, yPosition);
        yPosition += 8;
      }
    } else {
      doc.setTextColor(150);
      doc.text('Os dados para pagamento não foram informados.', margin, yPosition);
      yPosition += 8;
    }

    yPosition += 10;

    // Notes section (if exists)
    if (transaction.notes) {
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.setFont('helvetica', 'bold');
      doc.text('Observações', margin, yPosition);
      yPosition += 8;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(60);

      // Word wrap for notes
      const lines = doc.splitTextToSize(transaction.notes, pageWidth - margin * 2);
      lines.forEach((line: string) => {
        doc.text(line, margin, yPosition);
        yPosition += 6;
      });

      yPosition += 10;
    }

    // Add separator line before footer
    doc.setDrawColor(200);
    doc.setLineWidth(0.5);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 15;

    // Terms and conditions
    doc.setFontSize(10);
    doc.setTextColor(60);
    doc.setFont('helvetica', 'italic');
    const terms = [
      '• O pagamento deve ser realizado até a data de vencimento indicada.',
      '• Após o vencimento, serão aplicados juros de mora de 1% ao mês e multa de 2%.',
      '• Esta cobrança pode ser paga pelos métodos indicados acima.',
      '• Em caso de dúvidas, entre em contato conosco.'
    ];

    terms.forEach((term) => {
      const termLines = doc.splitTextToSize(term, pageWidth - margin * 2);
      termLines.forEach((line: string) => {
        doc.text(line, margin, yPosition);
        yPosition += 5;
      });
    });

    yPosition += 10;

    // Company signature and address
    doc.setFont('helvetica', 'normal');
    doc.text(`${companyName} - ${issueDate.toLocaleDateString('pt-BR')}`, margin, yPosition);
    yPosition += 8;

    if (companyDocument) {
      doc.text(`CNPJ: ${companyDocument}`, margin, yPosition);
      yPosition += 8;
    }

    if (companyAddress) {
      yPosition += 5;
      const addressLines = [
        [companyAddress.street, companyAddress.number, companyAddress.complement].filter(Boolean).join(', '),
        [companyAddress.neighborhood, companyAddress.city, companyAddress.state, companyAddress.zipCode].filter(Boolean).join(' - ')
      ].filter(Boolean);

      addressLines.forEach((line) => {
        doc.text(line, margin, yPosition);
        yPosition += 6;
      });
    }

    // Add page number in footer
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text('Página 1 de 1', pageWidth / 2, pageHeight - 10, { align: 'center' });

    // Generate filename
    const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const filename = `cobranca_${billNumber}_${timestamp}.pdf`;

    // Save the PDF
    doc.save(filename);
  } catch (error) {
    console.error('Error generating bill PDF:', error);
    throw new Error('Falha ao gerar o PDF da cobrança. Por favor, tente novamente.');
  }
}

/**
 * Alternative method: Generate PDF from HTML template
 */
export async function generateBillPDFFromHTML(
  elementId: string,
  filename: string
): Promise<void> {
  try {
    const element = document.getElementById(elementId);
    if (!element) {
      throw new Error('Element not found for PDF generation');
    }

    // Create canvas from HTML element
    const canvas = await html2canvas(element, {
      scale: 2,
      backgroundColor: '#ffffff',
      logging: false,
      useCORS: true,
      allowTaint: true,
    });

    // Create PDF
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight) * 200;
    const imgX = (pdfWidth - imgWidth * ratio) / 2;
    const imgY = 10;

    pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
    pdf.save(filename);
  } catch (error) {
    console.error('Error generating PDF from HTML:', error);
    throw new Error('Falha ao gerar o PDF. Por favor, tente novamente.');
  }
}

/**
 * Format currency value
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}